import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X, CreditCard } from 'lucide-react';
import StripeCheckout from '../payment/StripeProvider';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBox from '../common/NotificationBox';

type PlanStatus = 'online' | 'updating' | 'offline';

interface PlanStatuses {
  weekly: PlanStatus;
  monthly: PlanStatus;
  lifetime: PlanStatus;
}

interface PricingSectionProps {
  isRustPage?: boolean;
}

const PricingSection: React.FC<PricingSectionProps> = ({ isRustPage = false }) => {
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showError, setShowError] = useState(false);
  
  // Status indicators
  const statuses: PlanStatuses = {
    weekly: "online",
    monthly: "online", 
    lifetime: "online"
  };
  
  // Get status color
  const getStatusColor = (status: PlanStatus): string => {
    switch(status) {
      case 'online': return 'bg-green-400';
      case 'updating': return 'bg-yellow-400';
      case 'offline': return 'bg-red-400';
      default: return 'bg-gray-400';
    }
  };

  // Payment methods
  const paymentMethods = [
    { id: 'stripe', name: 'Stripe' },
    { id: 'cashapp', name: 'Cash App' },
    { id: 'btc', name: 'Bitcoin' },
    { id: 'eth', name: 'Ethereum' },
    { id: 'ltc', name: 'Litecoin' }
  ];

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectedPlan && (event.target as HTMLElement).closest('.payment-modal') === null) {
        setSelectedPlan(null);
      }
    };

    if (selectedPlan) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedPlan]);

  // Handle button click based on page
  const handleButtonClick = (plan: string) => {
    if (isRustPage) {
      setSelectedPlan(plan);
    } else {
      navigate('/products/rust');
    }
  };

  const handleStripeSuccess = () => {
    setShowPaymentModal(false);
    setShowStripeForm(false);
    // You might want to show a success message or redirect to the user panel
    navigate('/user-panel');
  };

  const handleStripeCancel = () => {
    setShowStripeForm(false);
  };

  const handlePaymentMethodClick = (methodId: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Handle different payment methods
    switch (methodId) {
      case 'stripe':
        setShowStripeForm(true);
        setShowPaymentModal(true);
        break;
      case 'cashapp':
        // Handle Cash App payment
        break;
      case 'btc':
        // Handle Bitcoin payment
        break;
      case 'eth':
        // Handle Ethereum payment
        break;
      case 'ltc':
        // Handle Litecoin payment
        break;
    }
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setShowStripeForm(false);
  };

  return (
    <div className="w-full relative">
      {/* Pricing container */}
      <div className="max-w-5xl mx-auto flex justify-center gap-4 relative z-10">
        {/* Weekly Plan */}
        <div className="relative">
          {/* Card container with hover effect */}
          <div 
            className={`w-56 transition-transform duration-300 ${hoveredPlan === 'weekly' ? 'scale-105' : 'scale-100'}`}
            onMouseEnter={() => setHoveredPlan('weekly')}
            onMouseLeave={() => setHoveredPlan(null)}
          >
            {/* Glow effect on hover */}
            {hoveredPlan === 'weekly' && (
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-xl blur-md opacity-50 animate-pulse"></div>
            )}
            
            {/* Card content */}
            <div className="relative bg-gradient-to-b from-gray-900/90 to-gray-800/70 border border-blue-500/40 rounded-xl overflow-hidden">
              {/* Status bar */}
              <div className="h-1 w-full bg-gradient-to-r from-transparent via-blue-500/30 to-transparent relative overflow-hidden">
                <div className={`absolute inset-0 ${getStatusColor(statuses.weekly)}/50`}></div>
              </div>
              
              <div className="p-5">
                {/* Price */}
                <div className="mb-1">
                  <div className="text-3xl font-bold text-white drop-shadow-[0_0_11px_rgba(255,255,255,0.22)] [text-shadow:0_0_7px_rgba(255,255,255,0.22)]">$9.99</div>
                  <div className="text-sm text-gray-400 flex items-center justify-between">
                    <span>weekly</span>
                    <span className="flex items-center">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(statuses.weekly)} mr-1`}></div>
                      <span className="text-xs">{statuses.weekly}</span>
                    </span>
                  </div>
                </div>
                
                <div className="w-12 h-px bg-blue-500/30 my-4"></div>
                
                {/* Features */}
                <div className="space-y-2 mb-6">
                  <div className="text-xs text-gray-400">
                    <span className="text-blue-400">•</span> All premium features
                  </div>
                  <div className="text-xs text-gray-400">
                    <span className="text-blue-400">•</span> Technical support
                  </div>
                </div>
                
                {/* Button */}
                <div className="relative">
                  {hoveredPlan === 'weekly' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-600/50 to-blue-600/0 rounded-md blur-md animate-pulse"></div>
                  )}
                  <button 
                    onClick={() => handleButtonClick('weekly')}
                    className="relative w-full bg-gradient-to-r from-blue-600/80 to-blue-500/80 hover:from-blue-500 hover:to-blue-600 text-white text-sm font-medium py-2 rounded-md backdrop-blur-sm transition-all duration-200 border border-blue-400/20 group flex items-center justify-center"
                  >
                    <span className="group-hover:translate-x-[-4px] transition-transform duration-200">Buy Now</span>
                    <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Monthly Plan */}
        <div className="relative">
          {/* Constant highlight effect for preferred option */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-xl opacity-0 animate-preferred-plan"></div>
          
          {/* Card container with hover effect */}
          <div 
            className={`w-56 transition-transform duration-300 ${hoveredPlan === 'monthly' ? 'scale-105' : 'scale-100'}`}
            onMouseEnter={() => setHoveredPlan('monthly')}
            onMouseLeave={() => setHoveredPlan(null)}
          >
            {/* Enhanced glow effect on hover */}
            {hoveredPlan === 'monthly' && (
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-xl blur-md opacity-50 animate-pulse"></div>
            )}
            
            {/* Card content */}
            <div className="relative bg-gradient-to-b from-gray-900/90 to-gray-800/70 border border-blue-500/40 rounded-xl overflow-hidden">
              {/* Enhanced status bar for monthly plan */}
              <div className="h-1 w-full bg-gradient-to-r from-transparent via-blue-500/50 to-transparent relative overflow-hidden">
                <div className={`absolute inset-0 ${getStatusColor(statuses.monthly)}/50`}></div>
              </div>
              
              <div className="p-5">
                {/* Price with preferred badge */}
                <div className="mb-1 relative">
                  <div className="text-3xl font-bold text-white drop-shadow-[0_0_11px_rgba(255,255,255,0.22)] [text-shadow:0_0_7px_rgba(255,255,255,0.22)]">$29.99</div>
                  <div className="text-sm text-gray-400 flex items-center justify-between">
                    <span>monthly</span>
                    <span className="flex items-center">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(statuses.monthly)} mr-1`}></div>
                      <span className="text-xs">{statuses.monthly}</span>
                    </span>
                  </div>
                </div>
                
                <div className="w-12 h-px bg-blue-500/30 my-4"></div>
                
                {/* Features */}
                <div className="space-y-2 mb-6">
                  <div className="text-xs text-gray-400">
                    <span className="text-blue-400">•</span> All premium features
                  </div>
                  <div className="text-xs text-gray-400">
                    <span className="text-blue-400">•</span> Technical support
                  </div>
                </div>
                
                {/* Button */}
                <div className="relative">
                  {hoveredPlan === 'monthly' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-600/50 to-blue-600/0 rounded-md blur-md animate-pulse"></div>
                  )}
                  <button 
                    onClick={() => handleButtonClick('monthly')}
                    className="relative w-full bg-gradient-to-r from-blue-600/80 to-blue-500/80 hover:from-blue-500 hover:to-blue-600 text-white text-sm font-medium py-2 rounded-md backdrop-blur-sm transition-all duration-200 border border-blue-400/20 group flex items-center justify-center"
                  >
                    <span className="group-hover:translate-x-[-4px] transition-transform duration-200">Buy Now</span>
                    <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Lifetime Plan */}
        <div className="relative">
          {/* Card container with hover effect */}
          <div 
            className={`w-56 transition-transform duration-300 ${hoveredPlan === 'lifetime' ? 'scale-105' : 'scale-100'}`}
            onMouseEnter={() => setHoveredPlan('lifetime')}
            onMouseLeave={() => setHoveredPlan(null)}
          >
            {/* Glow effect on hover */}
            {hoveredPlan === 'lifetime' && (
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-xl blur-md opacity-50 animate-pulse"></div>
            )}
            
            {/* Card content */}
            <div className="relative bg-gradient-to-b from-gray-900/90 to-gray-800/70 border border-blue-500/40 rounded-xl overflow-hidden">
              {/* Status bar */}
              <div className="h-1 w-full bg-gradient-to-r from-transparent via-blue-500/30 to-transparent relative overflow-hidden">
                <div className={`absolute inset-0 ${getStatusColor(statuses.lifetime)}/50`}></div>
              </div>
              
              <div className="p-5">
                {/* Price */}
                <div className="mb-1">
                  <div className="text-3xl font-bold text-white drop-shadow-[0_0_11px_rgba(255,255,255,0.22)] [text-shadow:0_0_7px_rgba(255,255,255,0.22)]">$179.99</div>
                  <div className="text-sm text-gray-400 flex items-center justify-between">
                    <span>lifetime</span>
                    <span className="flex items-center">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(statuses.lifetime)} mr-1`}></div>
                      <span className="text-xs">{statuses.lifetime}</span>
                    </span>
                  </div>
                </div>
                
                <div className="w-12 h-px bg-blue-500/30 my-4"></div>
                
                {/* Features */}
                <div className="space-y-2 mb-6">
                  <div className="text-xs text-gray-400">
                    <span className="text-blue-400">•</span> Permanent access
                  </div>
                  <div className="text-xs text-gray-400">
                    <span className="text-blue-400">•</span> 24/7 Priority support
                  </div>
                </div>
                
                {/* Button */}
                <div className="relative">
                  {hoveredPlan === 'lifetime' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-600/50 to-blue-600/0 rounded-md blur-md animate-pulse"></div>
                  )}
                  <button 
                    onClick={() => handleButtonClick('lifetime')}
                    className="relative w-full bg-gradient-to-r from-blue-600/80 to-blue-500/80 hover:from-blue-500 hover:to-blue-600 text-white text-sm font-medium py-2 rounded-md backdrop-blur-sm transition-all duration-200 border border-blue-400/20 group flex items-center justify-center"
                  >
                    <span className="group-hover:translate-x-[-4px] transition-transform duration-200">Buy Now</span>
                    <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal - Only show on Rust page */}
      {isRustPage && selectedPlan && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="payment-modal bg-[#0d1117]/95 border border-gray-700/40 rounded-xl p-8 w-[32rem] relative">
              {/* Close button */}
              <button 
                onClick={() => setSelectedPlan(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-300/80 mb-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.15)] [text-shadow:0_0_5px_rgba(255,255,255,0.15)]">Select Payment Method</h3>
                <p className="text-gray-500 text-sm">
                  {selectedPlan === 'weekly' && 'Weekly Plan - $9.99'}
                  {selectedPlan === 'monthly' && 'Monthly Plan - $29.99'}
                  {selectedPlan === 'lifetime' && 'Lifetime Plan - $179.99'}
                </p>
              </div>

              {/* Payment Methods Icons */}
              <div className="flex justify-center items-center gap-12">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => handlePaymentMethodClick(method.id)}
                    className="opacity-80 hover:opacity-100 transition-all duration-200 transform hover:scale-110"
                  >
                    <img 
                      src={`/images/payment-icons/${method.id}.png`} 
                      alt={method.name} 
                      className={`${method.id === 'stripe' ? 'w-[70px]' : method.id === 'cashapp' ? 'w-[105px]' : 'w-[37px]'} object-contain`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Payment Methods Section - Only show on home page */}
      {!isRustPage && (
        <div className="flex justify-center items-center gap-8 mt-8 relative z-10">
          {paymentMethods.map((method) => (
            <div 
              key={method.id} 
              className="h-6 opacity-80 hover:opacity-100 transition-all duration-200 transform hover:scale-110"
            >
              <img 
                src={`/images/payment-icons/${method.id}.png`} 
                alt={method.name} 
                className="h-6 w-auto" 
              />
            </div>
          ))}
        </div>
      )}

      {showPaymentModal && (
        // <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        //   <div className="bg-[#0d1117]/95 border border-gray-700/40 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl relative">
        //     <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl" />
        //     <div className="relative">
              <div>
                {showStripeForm && selectedPlan && (
                <StripeCheckout 
                  onSuccess={handleStripeSuccess} 
                  onCancel={handleStripeCancel}
                  onError={(error) => {
                    console.error(error)
                    setShowStripeForm(false)
                    setSelectedPlan(null)
                    setShowPaymentModal(false)
                    setShowError(true);
                  }}
                  planType={selectedPlan as 'weekly' | 'monthly' | 'lifetime'}
                  productName="Rust Script"
                />
              )}
        //     </div>
        //   </div>
        // </div>
      )}

    {showError && (
        <NotificationBox
          message="You already have an active subscription, please cancel it first."
          type="error"
          onClose={() => setShowError(false)}
        />
      )}

      <style>{`
        @keyframes preferredPlan {
          0%, 100% {
            opacity: 0.1;
            filter: blur(8px);
          }
          50% {
            opacity: 0.2;
            filter: blur(12px);
          }
        }
      `}</style>
    </div>
  );
};

export default PricingSection; 